/**
 * Flag message handler
 * Records start_flag/stop_flag/pending_flag; refreshes the message list on stop_flag.
 */
import type { AgentMessageResponse } from '@/types/formulation/formulation';
import type { StreamMessageHandler, StreamMessageData, HandlerContext } from '../types';

export const flagHandler: StreamMessageHandler = {
  name: 'flag',

  canHandle(data: StreamMessageData): boolean {
    return (
      data.business_type === 'start_flag' ||
      data.business_type === 'stop_flag' ||
      data.business_type === 'pending_flag'
    );
  },

  handle(data: StreamMessageData, ctx: HandlerContext): boolean {
    const { tool_key, business_type, content } = data;
    const { sessionIdValue, streamIteration, roundStartIso, deps } = ctx.streamContext;
    const { conversationData, sessionId, materialFormulationApiService } = deps;

    // 创建 flag 消息
    const flagMessage: AgentMessageResponse = {
      id: Date.now(), // 使用时间戳作为唯一 ID
      business_type: business_type as string,
      session_id: sessionIdValue,
      iteration: streamIteration,
      tool_key,
      content: content as { raw?: string } | undefined,
      tool_state: 'completed',
      gmt_created: roundStartIso,
    };

    // 添加到 conversationData
    const existingMessages = conversationData.value ?? [];
    conversationData.value = [...existingMessages, flagMessage];

    // stop_flag 时调用 listAgentMessages 刷新消息列表，并打开最后一个工具结果
    if (business_type === 'stop_flag') {
      const { tryAutoOpenLastToolResult } = deps;
      materialFormulationApiService
        .listAgentMessages(sessionIdValue)
        .then((messages) => {
          // 确保 session 未切换
          if (sessionId.value !== sessionIdValue) return;
          const existing = conversationData.value ?? [];
          const usedClientKeys = new Set<string>();
          conversationData.value = messages.map((serverMsg) => {
            const match = existing.find(
              (m) =>
                m.clientKey !== undefined &&
                !usedClientKeys.has(m.clientKey) &&
                m.business_type === serverMsg.business_type &&
                m.iteration === serverMsg.iteration &&
                m.tool_key === serverMsg.tool_key,
            );
            if (match && match.clientKey) {
              usedClientKeys.add(match.clientKey);
              return { ...serverMsg, clientKey: match.clientKey };
            }
            return { ...serverMsg, clientKey: String(serverMsg.id) };
          });
          tryAutoOpenLastToolResult(messages);
        })
        .catch((error) => {
          console.error('[Formulation] Failed to refresh messages on stop_flag:', error);
        });
    }

    return false;
  },
};
